import { memo } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import {
  countMissingMonths,
  formatCompactVisits,
  formatMonthLong,
  formatMonthTick,
  formatVisitsFull,
  type ChartPoint,
} from "./utils";

const trafficChartConfig = {
  visits: {
    label: "Visits",
    theme: { light: "hsl(173 58% 40%)", dark: "hsl(var(--chart-2))" },
  },
} satisfies ChartConfig;

interface TrafficTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: ChartPoint }>;
}

function TrafficTooltip({ active, payload }: TrafficTooltipProps) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  // A gap month has no value to report, so show nothing rather than "—".
  if (!point || point.visits === null) return null;

  return (
    <div className="rounded-lg border border-border bg-popover/95 px-2.5 py-1.5 shadow-lg backdrop-blur-sm">
      <div className="text-xs text-muted-foreground">
        {formatMonthLong(point.month)}
      </div>
      <div className="font-mono text-sm font-medium tabular-nums text-foreground">
        {formatVisitsFull(point.visits)} visits
      </div>
    </div>
  );
}

interface WebsiteTrafficChartProps {
  /** Gap-filled: one row per calendar month, null visits for missing months. */
  points: ChartPoint[];
  className?: string;
}

/**
 * Monthly traffic over time.
 *
 * `connectNulls={false}` plus explicit null rows is what makes gaps visible.
 * `type="linear"` rather than the monotone spline used elsewhere in the app:
 * splines invent local minima and maxima between points, which on sparse
 * third-party estimates would be fabricated shape.
 */
function WebsiteTrafficChart({ points, className }: WebsiteTrafficChartProps) {
  const missing = countMissingMonths(points);

  return (
    <div className="space-y-2">
      <ChartContainer
        config={trafficChartConfig}
        // aspect-auto is required: the container's base classes include
        // aspect-video, and tailwind-merge does not treat aspect-* and h-* as
        // conflicting, so a bare height would lose.
        className={className ?? "aspect-auto h-56 w-full"}
      >
        <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={formatMonthTick}
            interval="preserveStartEnd"
            minTickGap={24}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={(value) => formatCompactVisits(Number(value))}
            width={48}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            // Anchoring at zero is the honest choice for traffic; a dataMin
            // floor would exaggerate month-to-month variance on estimates.
            domain={[0, "auto"]}
            tick={{ fontSize: 11 }}
          />
          <ChartTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={<TrafficTooltip />}
          />
          <Line
            type="linear"
            dataKey="visits"
            stroke="var(--color-visits)"
            strokeWidth={2}
            // Dots mark real observations only, so a long unbroken stretch
            // without dots is still legible as interpolated.
            dot={{ r: 2, strokeWidth: 0, fill: "var(--color-visits)" }}
            activeDot={{ r: 4 }}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>

      {missing > 0 && (
        // The line is now continuous across gaps, so the caption is what keeps
        // it honest: it says which parts of the line were never measured.
        <p className="text-[11px] text-muted-foreground">
          {missing} {missing === 1 ? "month has" : "months have"} no provider
          coverage. The line is connected across{" "}
          {missing === 1 ? "it" : "them"}; only dots are measured months.
        </p>
      )}
    </div>
  );
}

export default memo(WebsiteTrafficChart);
